import React from "react";
import { Box, Grid, Typography, Link, IconButton, Divider, Stack } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Original imports preserved
import Mastercard from './img/mastercard.png';
import Visacard from './img/visa.png';
import AmericanExpress from './img/ae.png';
import Frimi from './img/frimi.png';
import Ipay from './img/ipay.jpg';
import Hela from './img/HELAPAY.png';

const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <Box 
      component="footer" 
      // Strictly keeping your exact original px and py values
      sx={{ backgroundColor: "#FAFAFA", pt: 6, pb: 3, px: { xs: 2, md: 7 } }}
    >
      <Grid container spacing={5} justifyContent="space-between">
        
        {/* Brand & Socials (Col 1) */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', mb: 2 }}
          >
            <Box component="span" sx={{ color: '#4F46E5' }}>BIZSTOR</Box>
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#64748B', lineHeight: 1.6, mb: 3, pr: { md: 4 } }}>
            Your trusted solution, offering smart accounting and management tools to elevate your business effortlessly.
          </Typography>

          <Stack direction="row" spacing={1}>
            <IconButton 
              component="a"
              href="https://www.facebook.com/share/15wFHDAFcg/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: '#ffffff', 
                border: '1px solid #E2E8F0',
                color: '#475569',
                '&:hover': { bgcolor: '#EFF6FF', color: '#2563EB', borderColor: '#BFDBFE' } 
              }}
            >
              <FacebookIcon fontSize="small" />
            </IconButton>

            <IconButton 
              component="a"
              href="https://whatsapp.com/channel/0029VbBFlyM4dTnDmwcSNV0N" 
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: '#ffffff', 
                border: '1px solid #E2E8F0',
                color: '#475569',
                '&:hover': { bgcolor: '#ECFDF5', color: '#10B981', borderColor: '#A7F3D0' } 
              }}
            >
              <WhatsAppIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Grid>

        {/* Company Links (Col 2) */}
        <Grid item xs={12} sm={6} md={2}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#0F172A', mb: 3 }}>
            Company
          </Typography>
          <Stack spacing={1.5}>
            {[
              { name: 'Plan & Pricing', url: '/plan-and-pricing' },
              { name: 'Features', url: '/features' }
            ].map((link) => (
              <Link 
                key={link.name}
                href={link.url} 
                underline="none" 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  color: '#64748B',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  '& .link-icon': { opacity: 0, transform: 'translateX(-5px)', transition: 'all 0.2s ease' },
                  '&:hover': { 
                    color: '#4F46E5',
                    transform: 'translateX(4px)',
                    '& .link-icon': { opacity: 1, transform: 'translateX(0)' }
                  } 
                }}
              >
                <ChevronRightIcon className="link-icon" fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                {link.name}
              </Link>
            ))}
          </Stack>
        </Grid>

        {/* Legal Links (Col 3) */}
        <Grid item xs={12} sm={6} md={2}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#0F172A', mb: 3 }}>
            Legal
          </Typography>
          <Stack spacing={1.5}>
            {[
              { name: 'Terms & Conditions', url: '/terms-and-condition' },
              { name: 'Privacy Policy', url: '/privacy-policy' },
              { name: 'Refund Policy', url: '/refund-policy' }
            ].map((link) => (
              <Link 
                key={link.name}
                href={link.url} 
                underline="none" 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  color: '#64748B',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  '& .link-icon': { opacity: 0, transform: 'translateX(-5px)', transition: 'all 0.2s ease' },
                  '&:hover': { 
                    color: '#4F46E5',
                    transform: 'translateX(4px)',
                    '& .link-icon': { opacity: 1, transform: 'translateX(0)' }
                  } 
                }}
              >
                <ChevronRightIcon className="link-icon" fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                {link.name}
              </Link>
            ))}
          </Stack>
        </Grid>

        {/* Support & Payment Box (Col 4) */}
        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#0F172A', mb: 3 }}>
            Support
          </Typography>
          
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.95rem' }}>
              Email us at:
            </Typography>
            <Link 
              href="mailto:support@bizstor.lk" 
              underline="hover" 
              sx={{ color: '#4F46E5', fontWeight: 600, fontSize: '0.95rem' }}
            >
              support@bizstor.lk
            </Link>
          </Box>

          {/* Payment Options Container */}
          <Box 
            sx={{ 
              p: 2.5, 
              bgcolor: '#ffffff', 
              borderRadius: '16px', 
              border: '1px solid #E2E8F0',
              display: 'inline-block',
              width: '100%',
              maxWidth: '300px'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ fontWeight: 700, color: '#475569', mb: 2, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              Supported Payments
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <img src={Visacard} alt="Visacard" style={{ width: 45, height: 'auto', objectFit: 'contain' }} />
              <img src={Mastercard} alt="Mastercard" style={{ width: 45, height: 'auto', objectFit: 'contain' }} />
              {/* <img src={Frimi} alt="Frimi" style={{ width: 45, height: 'auto', objectFit: 'contain' }} /> */}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* Footer Bottom */}
      <Box sx={{ mt: 6, textAlign: "center" }}>
        <Divider sx={{ mb: 3, borderColor: '#E2E8F0' }} />
        <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
          &copy; {year} BIZSTOR. All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;